import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Send, PartyPopper, Check } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { Captcha } from "@/components/site/Captcha";
import { PRESTATIONS, PARFUMS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  nom: "", prenom: "", telephone: "", email: "", date_evenement: "",
  adresse: "", nombre_personnes: "", type_evenement: "", saveur_autre: "",
  topping_sup: "", message: "", rappel_accepte: false, website: "",
};

const inputClass = "w-full rounded-2xl border border-beige bg-cream px-4 py-3 text-chocolate placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-strawberry/50 focus:border-strawberry transition";

export const DevisForm = () => {
  const [form, setForm] = useState(empty);
  const [saveursSel, setSaveursSel] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [captchaOk, setCaptchaOk] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleSaveur = (nom) =>
    setSaveursSel((prev) => (prev.includes(nom) ? prev.filter((s) => s !== nom) : [...prev, nom]));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nom || !form.telephone || !form.email) {
      toast.error("Merci de renseigner au moins vos nom, téléphone et email.");
      return;
    }
    if (!captchaOk) {
      toast.error("Merci de valider le calcul anti-robot.");
      return;
    }
    setLoading(true);
    try {
      const saveursFinal = [...saveursSel, form.saveur_autre].filter(Boolean).join(" — ");
      const { saveur_autre, ...rest } = form;
      await axios.post(`${API}/quote`, { ...rest, saveurs: saveursFinal, type: "devis" });
      setDone(true);
      setForm(empty);
      setSaveursSel([]);
      setCaptchaOk(false);
      toast.success("Votre demande de devis a bien été envoyée ! 🍦");
    } catch (err) {
      toast.error("Une erreur est survenue. Merci de réessayer ou de nous appeler.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="devis" className="py-20 sm:py-28 px-5 sm:px-8 bg-chocolate relative overflow-hidden grain">
      <div className="max-w-5xl mx-auto relative">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-sm tracking-widest uppercase font-semibold text-pastel">Demande de devis gratuit</span>
            <h2 className="mt-3 font-heading text-4xl sm:text-5xl font-medium text-cream">Obtenez votre devis personnalisé</h2>
            <p className="mt-4 text-cream/80">Remplissez ce formulaire, nous revenons vers vous rapidement avec une offre sur mesure. Sans engagement.</p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          {done ? (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="rounded-[2rem] bg-white p-12 text-center shadow-hover" data-testid="devis-success">
              <PartyPopper className="mx-auto text-strawberry mb-4" size={40} />
              <h3 className="font-heading text-3xl font-semibold text-chocolate">Merci !</h3>
              <p className="mt-3 text-cocoa">Votre demande a bien été reçue. Nous vous recontactons très vite pour votre devis personnalisé.</p>
              <button data-testid="devis-reset-btn" onClick={() => setDone(false)} className="mt-6 rounded-full bg-strawberry px-7 py-3 font-semibold text-white hover:bg-[#B83044] transition">
                Faire une nouvelle demande
              </button>
            </motion.div>
          ) : (
            <form onSubmit={submit} data-testid="devis-form" className="rounded-[2rem] bg-white p-6 sm:p-10 shadow-hover grid grid-cols-1 sm:grid-cols-2 gap-5">
              <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="absolute -left-[9999px] w-px h-px opacity-0" aria-hidden="true" />
              <div>
                <label className="block text-sm font-semibold text-chocolate mb-1.5">Nom *</label>
                <input data-testid="devis-nom" className={inputClass} value={form.nom} onChange={set("nom")} placeholder="Votre nom" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-chocolate mb-1.5">Prénom</label>
                <input data-testid="devis-prenom" className={inputClass} value={form.prenom} onChange={set("prenom")} placeholder="Votre prénom" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-chocolate mb-1.5">Téléphone *</label>
                <input data-testid="devis-telephone" className={inputClass} value={form.telephone} onChange={set("telephone")} placeholder="06 XX XX XX XX" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-chocolate mb-1.5">Email *</label>
                <input data-testid="devis-email" type="email" className={inputClass} value={form.email} onChange={set("email")} placeholder="vous@email.com" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-chocolate mb-1.5">Date de l'événement</label>
                <input data-testid="devis-date" type="date" className={inputClass} value={form.date_evenement} onChange={set("date_evenement")} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-chocolate mb-1.5">Nombre de personnes</label>
                <input data-testid="devis-personnes" type="number" min="1" className={inputClass} value={form.nombre_personnes} onChange={set("nombre_personnes")} placeholder="Ex : 80" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-chocolate mb-1.5">Adresse de l'événement</label>
                <input data-testid="devis-adresse" className={inputClass} value={form.adresse} onChange={set("adresse")} placeholder="Ville, code postal…" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-chocolate mb-1.5">Type d'événement</label>
                <select data-testid="devis-type" className={inputClass} value={form.type_evenement} onChange={set("type_evenement")}>
                  <option value="">Choisir…</option>
                  {PRESTATIONS.map((p) => <option key={p.titre} value={p.titre}>{p.titre}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-chocolate mb-2">Saveurs souhaitées (plusieurs choix possibles)</label>
                <div className="flex flex-wrap gap-3">
                  {PARFUMS.map((p) => {
                    const active = saveursSel.includes(p.nom);
                    return (
                      <button
                        type="button"
                        key={p.nom}
                        data-testid={`devis-saveur-${p.nom.toLowerCase()}`}
                        aria-pressed={active}
                        onClick={() => toggleSaveur(p.nom)}
                        className={`inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-semibold transition ${active ? "bg-strawberry text-white border-strawberry" : "bg-cream text-chocolate border-beige hover:border-strawberry"}`}
                      >
                        <span>{p.emoji}</span> {p.nom}
                        {active && <Check size={16} />}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-chocolate mb-1.5">Autre saveur souhaitée</label>
                <input data-testid="devis-saveur-autre" className={inputClass} value={form.saveur_autre} onChange={set("saveur_autre")} placeholder="Ex : pistache, mangue, café…" />
              </div>
              <div className="sm:col-span-2 rounded-2xl bg-pastelbg border border-beige px-5 py-5 text-center">
                <p className="text-lg sm:text-xl font-semibold text-chocolate">🍦 Tous les toppings et coulis sont <span className="text-strawberry">inclus</span> dans nos prestations !</p>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-chocolate mb-1.5">Topping ou coulis supplémentaire souhaité</label>
                <input data-testid="devis-topping-sup" className={inputClass} value={form.topping_sup} onChange={set("topping_sup")} placeholder="Un topping ou coulis particulier que vous aimeriez en plus ?" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-chocolate mb-1.5">Message</label>
                <textarea data-testid="devis-message" rows={4} className={inputClass} value={form.message} onChange={set("message")} placeholder="Parlez-nous de votre événement…" />
              </div>
              <div className="sm:col-span-2">
                <Captcha onChange={setCaptchaOk} testid="devis-captcha" />
              </div>
              <div className="sm:col-span-2">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="devis-rappel-checkbox"
                    checked={form.rappel_accepte}
                    onChange={(e) => setForm({ ...form, rappel_accepte: e.target.checked })}
                    className="mt-1 h-5 w-5 rounded border-beige accent-strawberry shrink-0"
                  />
                  <span className="text-sm text-cocoa">J'accepte d'être rappelé(e) par la société Moum's concernant ma demande.</span>
                </label>
              </div>
              <div className="sm:col-span-2 rounded-2xl bg-pastelbg border border-beige px-5 py-4">
                <p className="text-sm text-chocolate"><strong>Bon à savoir :</strong> un acompte de <strong>10%</strong> est demandé pour confirmer et bloquer la date de votre prestation.</p>
              </div>
              <div className="sm:col-span-2">
                <button data-testid="devis-submit" type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-strawberry px-8 py-4 font-semibold text-white shadow-hover hover:bg-[#B83044] hover:scale-[1.01] transition-all disabled:opacity-60">
                  <Send size={18} /> {loading ? "Envoi en cours…" : "Obtenir mon devis"}
                </button>
              </div>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
